import { useState, useRef, useEffect } from 'react';
import { Input } from '@/components/ui/input';
import { Search, MapPinIcon } from 'lucide-react';
import { AddressZ } from './AddressForm';
import { SentryMonitoringService } from '@libs/frontend-services/src/sentry-monitoring-service';
import { useApiIsLoaded, APIProvider } from '@vis.gl/react-google-maps';
import { GOOGLE_MAPS_API_KEY } from 'src/config';

// Declare Google Maps types
declare global {
  interface Window {
    google?: typeof google;
  }
}

interface GooglePlacePrediction {
  place_id: string;
  description: string;
  structured_formatting: {
    main_text: string;
    secondary_text: string;
  };
  types: string[];
}

interface GooglePlace {
  place_id: string;
  formatted_address: string;
  address_components?: {
    short_name: string;
    long_name: string;
    types: string[];
  }[];
}

interface GooglePlacesAutocompleteProps {
  value: string;
  onChange: (value: string) => void;
  onPlaceSelect: (place: GooglePlace, addressData: AddressZ) => void;
  placeholder?: string;
}

const GooglePlacesAutocompleteInternal = ({
  value,
  onChange,
  onPlaceSelect,
  placeholder = 'Start typing your address',
}: GooglePlacesAutocompleteProps) => {
  const [searchQuery, setSearchQuery] = useState(value);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [googlePlaces, setGooglePlaces] = useState<GooglePlacePrediction[]>([]);
  const [isLoadingPlaces, setIsLoadingPlaces] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const autocompleteServiceRef = useRef<google.maps.places.AutocompleteService | null>(null);
  const placesServiceRef = useRef<google.maps.places.PlacesService | null>(null);
  const sentryMonitoringService = new SentryMonitoringService();
  const isApiLoaded = useApiIsLoaded();

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        !inputRef.current?.contains(event.target as Node)
      ) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Initialize Google Maps services when API is loaded
  useEffect(() => {
    if (!isApiLoaded) {
      return;
    }

    // Poll for places library to be available
    const checkPlacesLibrary = () => {
      if (window.google?.maps?.places) {
        autocompleteServiceRef.current = new window.google.maps.places.AutocompleteService();
        placesServiceRef.current = new window.google.maps.places.PlacesService(
          document.createElement('div'),
        );
        return true;
      }
      return false;
    };

    // Try immediately
    if (checkPlacesLibrary()) {
      return;
    }

    // If not available, poll every 100ms for up to 5 seconds
    let attempts = 0;
    const maxAttempts = 50; // 5 seconds
    const interval = setInterval(() => {
      attempts++;

      if (checkPlacesLibrary()) {
        clearInterval(interval);
      } else if (attempts >= maxAttempts) {
        clearInterval(interval);
      }
    }, 100);

    return () => clearInterval(interval);
  }, [isApiLoaded]);

  // Sync external value changes
  useEffect(() => {
    if (value !== searchQuery) {
      setSearchQuery(value);
    }
  }, [value, searchQuery]);

  const fetchGooglePlaces = async (input: string) => {
    if (!autocompleteServiceRef.current || input.length < 2) {
      setGooglePlaces([]);
      return;
    }

    setIsLoadingPlaces(true);
    try {
      const request = {
        input,
        types: ['address'],
        componentRestrictions: { country: 'us' },
      };

      autocompleteServiceRef.current.getPlacePredictions(
        request,
        (
          predictions: GooglePlacePrediction[] | null,
          status: google.maps.places.PlacesServiceStatus,
        ) => {
          setIsLoadingPlaces(false);
          if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions) {
            setGooglePlaces(predictions.slice(0, 5));
          } else {
            setGooglePlaces([]);
          }
        },
      );
    } catch (error) {
      sentryMonitoringService.captureException(error as Error, {
        extra: {
          input,
        },
      });
      setIsLoadingPlaces(false);
      setGooglePlaces([]);
    }
  };

  const handleAddressSearch = (inputValue: string) => {
    setSearchQuery(inputValue);
    onChange(inputValue);
    setShowSuggestions(true);

    // Fetch Google Places predictions
    fetchGooglePlaces(inputValue);
  };

  const handleGooglePlaceSelect = async (prediction: GooglePlacePrediction) => {
    if (!placesServiceRef.current) return;

    setShowSuggestions(false);
    setSearchQuery(prediction.structured_formatting.main_text);

    // Get place details
    const request = {
      placeId: prediction.place_id,
      fields: ['address_components', 'place_id', 'formatted_address', 'geometry'],
    };

    placesServiceRef.current.getDetails(
      request,
      (
        place: google.maps.places.PlaceResult | null,
        status: google.maps.places.PlacesServiceStatus,
      ) => {
        if (
          status === window.google.maps.places.PlacesServiceStatus.OK &&
          place &&
          place.place_id
        ) {
          const getPlaceValue = (key: string) =>
            place.address_components?.find((c) => c.types.includes(key))?.short_name as string;

          const addressData: AddressZ = {
            line1: (getPlaceValue('street_number') || '') + ' ' + getPlaceValue('route'),
            city: getPlaceValue('locality'),
            state: getPlaceValue('administrative_area_level_1'),
            postalCode: getPlaceValue('postal_code'),
            country: getPlaceValue('country') || 'US',
            line2: '',
            type: 'delivery',
          };

          const googlePlace: GooglePlace = {
            place_id: place.place_id,
            formatted_address: place.formatted_address || '',
            address_components: place.address_components,
          };

          onPlaceSelect(googlePlace, addressData);
        }
      },
    );
  };

  const handleInputFocus = () => {
    setShowSuggestions(true);
  };

  return (
    <div className="relative">
      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 z-10" />
      <Input
        ref={inputRef}
        type="text"
        placeholder={placeholder}
        value={searchQuery}
        onChange={(e) => handleAddressSearch(e.target.value)}
        onFocus={handleInputFocus}
        className="h-10 text-sm border-2 border-gray-200 rounded-lg pl-10 pr-4 transition-colors"
        autoComplete="off"
      />

      {/* Google Places Dropdown - Single container for both states */}
      {showSuggestions && (googlePlaces.length > 0 || isLoadingPlaces) && (
        <div
          ref={dropdownRef}
          className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-lg z-10 max-h-80 overflow-y-auto"
        >
          {isLoadingPlaces ? (
            <div className="px-4 py-3 text-center text-gray-500 text-sm">
              <Search className="w-4 h-4 mx-auto mb-1 animate-spin" />
              Searching addresses...
            </div>
          ) : (
            <div className="rounded-t-xl">
              <div className="flex items-center px-4 py-3 text-xs font-medium text-gray-500 bg-gray-50 rounded-t-xl">
                <Search className="w-3 h-3 mr-2" />
                Address Suggestions
              </div>
              <div className="py-1">
                {googlePlaces.map((place) => (
                  <button
                    key={place.place_id}
                    onClick={() => handleGooglePlaceSelect(place)}
                    className="w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors group flex items-center"
                  >
                    <div className="flex items-center justify-center w-8 h-8 bg-blue-50 rounded-full mr-3 group-hover:bg-blue-100 transition-colors">
                      <MapPinIcon className="w-3 h-3 text-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-gray-900 truncate">
                        {place.structured_formatting.main_text}
                      </div>
                      <div className="text-xs text-gray-500 truncate">
                        {place.structured_formatting.secondary_text}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Wrapper component that provides APIProvider
export const GooglePlacesAutocomplete = (props: GooglePlacesAutocompleteProps) => {
  return (
    <APIProvider apiKey={GOOGLE_MAPS_API_KEY || ''} libraries={['places', 'geometry']}>
      <GooglePlacesAutocompleteInternal {...props} />
    </APIProvider>
  );
};
